<template>
  <div class="wa" :class="{ 'wa--mobile-chat': mobileShowChat }">
    <wa-sidebar
      :conversations="filteredConversations"
      :selected-phone="selectedPhone"
      :loading="loading"
      :firestore-error="firestoreError"
      :search-query="searchQuery"
      @select-conversation="selectConversation"
      @update:search-query="searchQuery = $event"
    />

    <main class="wa__main">
      <div v-if="!selectedConversation" class="wa__empty">
        <i class="fas fa-comments wa__empty-icon"></i>
        <h3 class="wa__empty-title">Selecciona una conversación</h3>
        <p class="wa__empty-subtitle">
          Elige una conversación de la lista para ver el hilo del agente CRESA
        </p>
      </div>

      <template v-else>
        <wa-header
          :conversation="selectedConversation"
          :employee-name="resolveEmployeeName(selectedConversation)"
          :mode="selectedConversation?.mode ?? 'bot'"
          :toggling-mode="togglingMode"
          :is-mobile="isMobile"
          :show-info="showInfo"
          @back="mobileShowChat = false"
          @toggle-info="showInfo = !showInfo"
          @pause-bot="handlePauseBot"
          @resume-bot="handleResumeBot"
        />

        <div class="wa__body">
          <wa-messages
            ref="messagesEl"
            :grouped-messages="groupedMessages"
            :typing="typing"
            :chat-bg="chatBg"
          />

          <transition name="wa-slide">
            <wa-info-panel
              v-if="showInfo"
              :conversation="selectedConversation"
              :employee-name="resolveEmployeeName(selectedConversation)"
              @close="showInfo = false"
            />
          </transition>
        </div>

        <wa-input-bar
          :sending="sending"
          :is-human-mode="selectedConversation?.mode === 'human'"
          @send-message="sendMessage"
          @request-pause="handlePauseBot"
        />
      </template>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import { useStore } from "vuex";
import firebase from "firebase/compat/app";
import {
  getFirestore,
  collection,
  doc,
  onSnapshot,
  getDoc,
} from "firebase/firestore";

import WaSidebar from "./components/Bot/WaSidebar.vue";
import WaHeader from "./components/Bot/WaHeader.vue";
import WaMessages from "./components/Bot/WaMessages.vue";
import WaInputBar from "./components/Bot/WaInputBar.vue";
import WaInfoPanel from "./components/Bot/WaInfoPanel.vue";

const store = useStore();
const chatBg = "/img/Xtrim/background.webp";
const ECUADOR_TZ = "America/Guayaquil";

const conversations = ref([]);
const selectedPhone = ref(null);
const selectedConversation = ref(null);
const searchQuery = ref("");
const sending = ref(false);
const typing = ref(false);
const togglingMode = ref(false);
const showInfo = ref(false);
const loading = ref(true);
const mobileShowChat = ref(false);
const optimisticMessages = ref([]);
const firestoreError = ref(null);
const windowWidth = ref(window.innerWidth);
const profileNameCache = ref({});

const messagesEl = ref(null);

let unsubscribeList = null;
let unsubscribeDoc = null;

const db = getFirestore(firebase.app(), "distributors");

function handleResize() {
  windowWidth.value = window.innerWidth;
}

async function resolveMissingProfileNames(list) {
  const missingIds = [
    ...new Set(
      list
        .filter((c) => {
          const hasEmbedded =
            c.profile?.names || c.profile?.lastnames || c.employeeDisplayName;
          return c.employeeId && !hasEmbedded && !profileNameCache.value[c.employeeId];
        })
        .map((c) => c.employeeId),
    ),
  ];

  if (missingIds.length === 0) return;

  await Promise.all(
    missingIds.map(async (id) => {
      try {
        const snap = await getDoc(doc(db, "profiles", id));
        if (!snap.exists()) {
          profileNameCache.value = {
            ...profileNameCache.value,
            [id]: null,
          };
          return;
        }
        const data = snap.data();
        const name = `${data.names || ""} ${data.lastnames || ""}`.trim();
        profileNameCache.value = {
          ...profileNameCache.value,
          [id]: name || null,
        };
      } catch (err) {
        console.error("[cresa-bot] Error resolving profile:", id, err);
      }
    }),
  );
}

function enrichConversation(raw) {
  const embedded = `${raw.profile?.names || ""} ${raw.profile?.lastnames || ""}`.trim();
  const cached = raw.employeeId
    ? profileNameCache.value[raw.employeeId]
    : null;
  return {
    ...raw,
    employeeDisplayName:
      embedded || cached || raw.employeeId || raw.phoneNumber || "—",
  };
}

onMounted(() => {
  window.addEventListener("resize", handleResize);

  unsubscribeList = onSnapshot(
    collection(db, "agentChats"),
    async (snapshot) => {
      const list = snapshot.docs.map((d) => ({
        phoneNumber: d.id,
        ...d.data(),
      }));

      await resolveMissingProfileNames(list);
      conversations.value = list.map(enrichConversation);
      loading.value = false;

      // Keep sidebar selection in sync if the selected phone still exists
      if (
        selectedPhone.value &&
        !list.some((c) => c.phoneNumber === selectedPhone.value)
      ) {
        selectedPhone.value = null;
        selectedConversation.value = null;
        if (unsubscribeDoc) {
          unsubscribeDoc();
          unsubscribeDoc = null;
        }
      }
    },
    (err) => {
      console.error("[cresa-bot] List listener error:", err);
      firestoreError.value = err.message || String(err);
      loading.value = false;
    },
  );
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  if (unsubscribeList) unsubscribeList();
  if (unsubscribeDoc) unsubscribeDoc();
});

const isMobile = computed(() => windowWidth.value < 768);

const filteredConversations = computed(() => {
  let list = [...conversations.value];
  const q = searchQuery.value.toLowerCase().trim();
  if (q) {
    list = list.filter(
      (c) =>
        (c.employeeDisplayName || "").toLowerCase().includes(q) ||
        (c.phoneNumber || "").toLowerCase().includes(q) ||
        (c.employeeId || "").toLowerCase().includes(q) ||
        (c.profile?.branch || "").toLowerCase().includes(q),
    );
  }
  list.sort(
    (a, b) =>
      toMs(b.lastMessageAt || b.updatedAt) -
      toMs(a.lastMessageAt || a.updatedAt),
  );
  return list;
});

const visibleMessages = computed(() => {
  const messages = selectedConversation.value?.messages || [];
  const items = [];

  for (const msg of messages) {
    if (isToolMessage(msg.content)) continue;
    if (Array.isArray(msg.content)) continue;
    if (msg.isVoiceNote || msg.type === "audio") {
      items.push(msg);
      continue;
    }
    if (msg.type === "location") {
      items.push(msg);
      continue;
    }
    const text = extractText(msg.content);
    if (!text) continue;
    items.push(msg);
  }

  for (const opt of optimisticMessages.value) {
    items.push({
      role: opt.role,
      content: opt.text,
      timestamp: opt.timestamp,
      type: "text",
      ...(opt.sentByAdvisor && {
        sentByAdvisor: true,
        advisorName: opt.advisorName,
      }),
    });
  }

  return items;
});

const groupedMessages = computed(() => {
  const items = [];
  let lastDate = "";

  for (const msg of visibleMessages.value) {
    if (Array.isArray(msg.content)) continue;

    const ts = msg.timestamp;
    const dateKey = ts
      ? new Date(ts).toLocaleDateString("es-EC", { timeZone: ECUADOR_TZ })
      : null;

    if (dateKey && dateKey !== lastDate) {
      items.push({ type: "date-separator", label: formatDateLabel(ts) });
      lastDate = dateKey;
    }

    items.push({ type: "message", message: msg });
  }

  return items;
});

watch(
  () => selectedConversation.value?.messages?.length,
  (newLen, oldLen) => {
    if (newLen == null) return;
    if (newLen !== oldLen) {
      optimisticMessages.value = [];
      if (typing.value && newLen > (oldLen || 0)) {
        typing.value = false;
      }
      nextTick(scrollToBottom);
    }
  },
);

function selectConversation(conv) {
  selectedPhone.value = conv.phoneNumber;
  showInfo.value = false;
  optimisticMessages.value = [];
  typing.value = false;
  mobileShowChat.value = true;

  if (unsubscribeDoc) {
    unsubscribeDoc();
    unsubscribeDoc = null;
  }

  unsubscribeDoc = onSnapshot(
    doc(db, "agentChats", conv.phoneNumber),
    async (snap) => {
      if (!snap.exists()) {
        selectedConversation.value = null;
        return;
      }
      const data = { phoneNumber: snap.id, ...snap.data() };
      await resolveMissingProfileNames([data]);
      selectedConversation.value = enrichConversation(data);
      nextTick(scrollToBottom);
    },
    (err) => {
      console.error("[cresa-bot] Doc listener error:", err);
    },
  );

  nextTick(scrollToBottom);
}

function scrollToBottom() {
  if (messagesEl.value?.scrollToBottom) {
    messagesEl.value.scrollToBottom();
  }
}

function resolveEmployeeName(conv) {
  if (!conv) return "—";
  return (
    conv.employeeDisplayName ||
    `${conv.profile?.names || ""} ${conv.profile?.lastnames || ""}`.trim() ||
    conv.employeeId ||
    conv.phoneNumber ||
    "—"
  );
}

async function sendMessage(text) {
  const trimmed = (text || "").trim();
  if (!trimmed || sending.value || !selectedPhone.value) return;

  if (selectedConversation.value?.mode !== "human") return;

  const currentUser = store.state.user;
  const advisorName = currentUser
    ? `${currentUser.names || ""} ${currentUser.lastnames || ""}`.trim() ||
      currentUser.displayName ||
      "Asesor"
    : "Asesor";

  optimisticMessages.value.push({
    role: "assistant",
    text: trimmed,
    timestamp: Date.now(),
    sentByAdvisor: true,
    advisorName,
  });
  sending.value = true;
  nextTick(scrollToBottom);

  try {
    await callCresaAgent(
      "cresaSendAdvisorMessage",
      {
        phoneNumber: selectedPhone.value,
        message: trimmed,
        advisorName,
      },
      { "x-advisor-mode": "true" },
    );
  } catch (err) {
    console.error("[cresa-bot] Advisor send error:", err);
    optimisticMessages.value.push({
      role: "assistant",
      text: `Error: ${err.message}`,
      timestamp: Date.now(),
    });
  } finally {
    sending.value = false;
  }
}

async function handlePauseBot() {
  togglingMode.value = true;
  try {
    const currentUser = store.state.user;
    const advisorName = currentUser
      ? `${currentUser.names || ""} ${currentUser.lastnames || ""}`.trim() ||
        currentUser.displayName ||
        "Asesor"
      : "Asesor";
    const advisorUserId = currentUser?.userId ?? "unknown";
    await callCresaAgent("cresaPauseBotHandler", {
      phoneNumber: selectedPhone.value,
      advisorName,
      advisorUserId,
    });
  } catch (err) {
    console.error("[cresa-bot] pause error:", err);
  } finally {
    togglingMode.value = false;
  }
}

async function handleResumeBot() {
  togglingMode.value = true;
  try {
    await callCresaAgent("cresaResumeBotHandler", {
      phoneNumber: selectedPhone.value,
    });
  } catch (err) {
    console.error("[cresa-bot] resume error:", err);
  } finally {
    togglingMode.value = false;
  }
}

function botBaseUrl() {
  if (process.env.VUE_APP_BOT_TEST_URL) {
    return process.env.VUE_APP_BOT_TEST_URL.replace(/\/$/, "");
  }
  const projectId = process.env.VUE_APP_FIREBASE_PROJECT_ID;
  const isLocal =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  if (isLocal && projectId) {
    return `http://127.0.0.1:5001/${projectId}/us-central1`;
  }
  if (projectId) {
    return `https://us-central1-${projectId}.cloudfunctions.net`;
  }
  return "";
}

async function callCresaAgent(path, body, extraHeaders = {}) {
  const base = botBaseUrl();
  if (!base) {
    throw new Error(
      "Configura VUE_APP_BOT_TEST_URL o VUE_APP_FIREBASE_PROJECT_ID",
    );
  }
  const res = await fetch(`${base}/${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-bot-mode": "true",
      ...extraHeaders,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
}

function isToolMessage(content) {
  if (!Array.isArray(content)) return false;
  return content.some((b) => b.type === "tool_use" || b.type === "tool_result");
}

function extractText(content) {
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    return content
      .filter((b) => b.type === "text")
      .map((b) => b.text)
      .join("\n");
  }
  return "";
}

function toMs(timestamp) {
  if (!timestamp) return 0;
  if (typeof timestamp.toMillis === "function") return timestamp.toMillis();
  if (timestamp.seconds) return timestamp.seconds * 1000;
  return new Date(timestamp).getTime() || 0;
}

function formatDateLabel(timestamp) {
  if (!timestamp) return "";
  const ms = toMs(timestamp);

  const todayKey = new Date().toLocaleDateString("es-EC", {
    timeZone: ECUADOR_TZ,
  });
  const msgKey = new Date(ms).toLocaleDateString("es-EC", {
    timeZone: ECUADOR_TZ,
  });
  if (msgKey === todayKey) return "Hoy";

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayKey = yesterday.toLocaleDateString("es-EC", {
    timeZone: ECUADOR_TZ,
  });
  if (msgKey === yesterdayKey) return "Ayer";

  return new Date(ms).toLocaleDateString("es-EC", {
    timeZone: ECUADOR_TZ,
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
}
</script>

<style lang="scss">
@import "./components/Bot/BotTestTab.scss";
</style>
